
import { defineComponent, computed, onMounted, onUnmounted, ref } from "vue";
import CommonDialog from "@/components/common/CommonDialog.vue";
import CommonOKDialog from "@/components/common/CommonOKDialog.vue";
import store from "@/store";
import router from "@/router";
import NativeClient, { TitleInfo } from "@/client/native-client";
import { useApiErrorHandler } from "@/use/use-api-error-handler";
import { useApiErrorDialog } from "@/use/use-api-error-dialog";
import {
  FavoriteRecipeClip,
  FavoriteRecipeClips,
  HTMLDivElementEvent
} from "@/types/favorites";
import {
  isEndOfScrollBottom,
  createFavoriteRecipesToCommonRecipes
} from "@/utils/favorites-util";
import CommonRecipeList from "@/components/commonRecipe/CommonRecipeList.vue";
import { useLog } from "@/use/use-log";
import { PAGE_ID } from "@/const/log-const";
import MyListFolderOperation from "@/components/favorites/MyListFolderOperation.vue";
import FavoritesFolderNameInputDialog from "@/components/favorites/FavoritesFolderNameInputDialog.vue";
import { ApiError, BeaconLogInfo, UpdatePageInfo } from "@/types/common";
import { COMMON_LIMIT_NUM } from "@/const/limit_const";
import { COMMON_MESSAGE_STRING } from "@/const/message_const";
import CommonUtil, { isValidApiAccessToken } from "@/utils/common-utils";

export default defineComponent({
  name: "MyListFolderPage",
  components: {
    CommonDialog,
    CommonOKDialog,
    MyListFolderOperation,
    FavoritesFolderNameInputDialog,
    CommonRecipeList
  },
  setup() {
    const { setLogId, outputPageLogs } = useLog();

    // ログIDの設定
    setLogId(PAGE_ID.FAVORITES_MYLIST_FOLDER);

    // レシピ表示は、削除ボタン付き
    const deleteMode = 1;
    NativeClient.sendDebugLog("MyListFolderPage setupstart");
    CommonUtil.scroll({ top: 0, left: 0 });
    // テキスト
    const operationAppendText = "レシピを追加する";
    const folderDeleteMessage = "フォルダを\n削除してよろしいですか？";
    const recipeDeleteMessage = "お気に入りのリストから\n削除してよろしいですか？";
    const dialogMessageForRecipeLimitOver = "レシピは99個までしか登録する\nことが出来ません。";
    const dialogMessageCompleteFolderDelete = "フォルダを削除しました";
    const dialogMessageForCompleteRecipeDelete = "レシピを削除しました";
    const dialogMessageForLimitOverFolderName = COMMON_MESSAGE_STRING.FAVORITES_FOLDER_NAME_LIMIT_OVER;
    const okText = "OK";

    // ダイアログ表示関連リアクティブ用
    class MyListFolderPageDialogType {
      type: number; // 0:ダイアログ表示なし、1:フォルダ操作ダイアログ、2:フォルダ削除ダイアログ、3:フォルダ名変更ダイアログ、4:レシピ削除ダイアログ、5:フォルダ削除完了メッセージ、6:レシピ削除完了メッセージ、7:レシピ登録上限超メッセージ、8:フォルダ名文字数上限超メッセージ
      folderName: string;
      recipeId: number;
      constructor(type: number, folderName: string, recipeId: number) {
        this.type = type;
        this.folderName = folderName;
        this.recipeId = recipeId;
      }
    }
    const refDialog = ref(new MyListFolderPageDialogType(0, "", 0));

    // WebAPIのリクエスト状況
    class MyListFolderPageRequestType {
      type: number; // 0:なし、1:リスト、2:フォルダ削除、3:フォルダ名変更、4:レシピ削除、 5:追加読み込み
      folderName: string; // 変更後のフォルダ名
      recipeId: number; // 削除対象のレシピ
      constructor(type: number, folderName: string, recipeId: number) {
        this.type = type;
        this.folderName = folderName;
        this.recipeId = recipeId;
      }
    }
    const refRequestType = ref(new MyListFolderPageRequestType(0, "", 0));

    // 表示更新のためのリアクティブ
    const updateDisplayKey = ref(0);
    const updateDisplay = () => {
      NativeClient.sendDebugLog("MyListFolderPage updateDisplay");
      if (updateDisplayKey.value == 100) {
        updateDisplayKey.value = 0;
      } else {
        updateDisplayKey.value++;
      }
    };

    // フォルダ名入力のためのリアクティブ
    const updateInputFolderNameKey = ref(0);
    const updateInputFolderName = () => {
      if (updateInputFolderNameKey.value == 100) {
        updateInputFolderNameKey.value = 0;
      } else {
        updateInputFolderNameKey.value++;
      }
    };

    // urlから選択されたフォルダーIdを取得(folderIdが指定されていなければ、「最近追加したもの」)
    let currentId = 0;
    let isRecentList = false;
    let currentFolderName = "";
    if (router.currentRoute.value.query.folderId == null) {
      isRecentList = true;
      currentFolderName = "最近追加したもの";
    } else {
      currentId = Number(router.currentRoute.value.query.folderId);
      const folderName = store.getters["favorites/getFolderName"];
      if (folderName == null) {
        NativeClient.sendDebugLog("MyListFolderPage folderName not exist");
        currentFolderName = "";
      } else {
        NativeClient.sendDebugLog("MyListFolderPage folderName exist = " + folderName);
        currentFolderName = folderName;
      }
    }
    const refCurrentFolderName = ref(currentFolderName);
    NativeClient.sendDebugLog("MyListFolderPage currentFolderName = " + currentFolderName);

    // タイトル行の設定
    NativeClient.setTitle(new TitleInfo(false, currentFolderName));

    // 一覧クリア
    const clearList = () => {
      if (isRecentList) {
        // マイリスト
        store.dispatch("favorites/updateMyList", null);
      } else {
        // フォルダのレシピ一覧
        store.dispatch("favorites/updateFolderRecipeList", null);
      }
    };

    //
    // API実行
    //
    let totalCount = 0;
    let recipeCount = 0;
    const getRecipeList = async () => {
      if (isRecentList) {
        // マイリスト
        NativeClient.sendDebugLog("MyListFolderPage getMyList");
        store.dispatch("common/startLoading");
        await NativeClient.getApiParams();
        if (isValidApiAccessToken()) {
          refRequestType.value = new MyListFolderPageRequestType(1, "", 0);
          await store.dispatch("favorites/getMyList");
          NativeClient.sendDebugLog("MyListFolderPage getMyList end");
          const result: FavoriteRecipeClips = store.getters["favorites/getMyList"];
          totalCount = result.totalCount;
          recipeCount = result.clips.length;
          updateDisplay();
        }
      } else {
        // フォルダのレシピ一覧
        NativeClient.sendDebugLog("MyListFolderPage getFolderRecipeList");
        store.dispatch("common/startLoading");
        await NativeClient.getApiParams();
        if (isValidApiAccessToken()) {
          refRequestType.value = new MyListFolderPageRequestType(1, "", 0);
          await store.dispatch("favorites/getFolderRecipeList", currentId);
          NativeClient.sendDebugLog("MyListFolderPage getFolderRecipeList end");
          const result: FavoriteRecipeClips = store.getters["favorites/getFolderRecipeList"];
          totalCount = result.totalCount;
          recipeCount = result.clips.length;
          updateDisplay();
        }
      }
      // リクエスト完了
      refRequestType.value = new MyListFolderPageRequestType(0, "", 0);
      store.dispatch("common/stopLoading");
    };

    // フォルダのレシピ一覧を初期化して取得
    const initializeAndGetRecipeList = async () => {
      NativeClient.sendDebugLog("MyListFolderPage initializeAndGetRecipeList");
      totalCount = 0;
      recipeCount = 0;
      // マイリスト初期化
      if (isRecentList) {
        await store.dispatch("favorites/updateMyList", null);
      } else {
        await store.dispatch("favorites/updateFolderRecipeList", null);
      }
      getRecipeList();
    };

    // フォルダ削除
    const deleteFolder = async () => {
      NativeClient.sendDebugLog("MyListFolderPage deleteFolder");
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      if (isValidApiAccessToken()) {
        refRequestType.value = new MyListFolderPageRequestType(2, "", 0);
        await store.dispatch("favorites/deleteFolder", currentId);
        NativeClient.sendDebugLog("MyListFolderPage deleteFolder end");
        // 削除完了
        refDialog.value = new MyListFolderPageDialogType(5, "", 0);
      }
      store.dispatch("common/stopLoading");
    };

    // フォルダ名変更
    const renameFolder = async (folderName: string) => {
      NativeClient.sendDebugLog("MyListFolderPage renameFolder");
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      if (isValidApiAccessToken()) {
        refRequestType.value = new MyListFolderPageRequestType(3, folderName, 0);
        await store.dispatch("favorites/renameFolder", { folderId: currentId, folderName: folderName });
        NativeClient.sendDebugLog("MyListFolderPage renameFolder end folderName = " + folderName);
        // 変更完了
        currentFolderName = folderName;
        refCurrentFolderName.value = folderName;
        store.dispatch("favorites/updateFolderName", folderName);
        NativeClient.setTitle(new TitleInfo(false, currentFolderName));
      }
      store.dispatch("common/stopLoading");
    };

    // レシピ削除
    const deleteRecipe = async (recipeId: number) => {
      NativeClient.sendDebugLog("MyListFolderPage deleteFolder");
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      if (isValidApiAccessToken()) {
        refRequestType.value = new MyListFolderPageRequestType(2, "", 0);
        if (isRecentList) {
          await store.dispatch("favorites/deleteRecipe", recipeId);
        } else {
          await store.dispatch("favorites/deleteFolderRecipe",  { folderId: currentId, recipeIds: [recipeId] });
        }
        NativeClient.sendDebugLog("MyListFolderPage deleteFolder end");
        // 削除完了
        refDialog.value = new MyListFolderPageDialogType(6, "", 0);
      }
      store.dispatch("common/stopLoading");

      // 削除完了
      refDialog.value = new MyListFolderPageDialogType(6, "", 0);

      // Nativeへ通知
      if (isRecentList) {
        NativeClient.requestReloadMylist();
      }
    };

    // 実行
    const execute = async () => {
      NativeClient.sendDebugLog("MyListFolderPage execute()");
      getRecipeList();
    };

    // Nativeからの更新要求
    const updatePage = (info: UpdatePageInfo) => {
      NativeClient.sendDebugLog("MyListFolderPage updatePage()");
      if (info.myList) {
        NativeClient.sendDebugLog("MyListFolderPage updatePage() update mylist");
        initializeAndGetRecipeList();
      }
    };

    // 表示状態の制御
    let isDisplayed = false;
    onMounted(() => {
      isDisplayed = true;

      // フォルダー・レシピ一覧を初期化
      NativeClient.registUpdatePageListener(updatePage);

      execute();

      // Beacon Log
      const logInfo = new BeaconLogInfo(PAGE_ID.FAVORITES_MYLIST_RECENT_LIST, null, []);
      if (!isRecentList) {
        logInfo.pageId = PAGE_ID.FAVORITES_MYLIST_FOLDER;
      }
      NativeClient.sendViewedLog(logInfo)
    });
    onUnmounted(() => {
      isDisplayed = false;
      clearList();
    });

    // スクロール検知
    const onScroll = (event: HTMLDivElementEvent) => {
      NativeClient.sendDebugLog("MyListSelectPage onScroll totalCount = " + totalCount + "recipeCount = " + recipeCount);
      if (
        isEndOfScrollBottom(event) &&
        (refRequestType.value.type == 0) &&
        (recipeCount < totalCount)
      ) {
        NativeClient.sendDebugLog("MyListSelectPage onScroll detect");
        refRequestType.value = new MyListFolderPageRequestType(5, "", 0);
        getRecipeList();
      }
    };

    // レシピ一覧の部品のインターフェースに合わせて、配列を生成する
    const recipeList = computed(() => {
      let clips: Array<FavoriteRecipeClip> = [];
      NativeClient.sendDebugLog("MyListFolderPage recipeList");
      if (isRecentList) {
        const myList: FavoriteRecipeClips = store.getters["favorites/getMyList"];
        NativeClient.sendDebugLog("MyListFolderPage recipeList recentList");
        if (myList != null) {
          clips = myList.clips;
        } else {
          NativeClient.sendDebugLog("MyListFolderPage recipeList recentList is Null");
          return null;
        }
      } else {
        const folderRecipeList: FavoriteRecipeClips = store.getters["favorites/getFolderRecipeList"];
        NativeClient.sendDebugLog("MyListFolderPage recipeList folderRecipeList");
        if (folderRecipeList != null) {
          clips = folderRecipeList.clips;
        } else {
          NativeClient.sendDebugLog("MyListFolderPage recipeList folderRecipeList is Null");
          return null;
        }
      }
      const recipeList = createFavoriteRecipesToCommonRecipes(clips);
      return recipeList;
    });

    // レシピ追加ボタンの表示制御
    const styleFolderOperation = computed(() => {
      if (isRecentList) {
        // 全てのマイリスト
        return { display: "none" };
      }
      return { display: "flex" };
    });
    const styleBodySpacer = computed(() => {
      if (isRecentList) {
        return { display: "none" };
      } else {
        return  { display: "inline-block" };
      }
    });

    // 操作確認メッセージのダイアログ
    const showDialog = computed(() => {
      let tempShowDialog = false;
      switch (refDialog.value.type) {
        case 2:
          tempShowDialog = true;
          break;
        case 4:
          tempShowDialog = true;
          break;
        default:
          break;
      }
      return tempShowDialog;
    });
    const deleteMessage = computed(() => {
      let tempMessage = "";
      switch (refDialog.value.type) {
        case 2:
          tempMessage = folderDeleteMessage;
          break;
        case 4:
          tempMessage = recipeDeleteMessage;
          break;
        default:
          break;
      }
      return tempMessage;
    });

    const cancelHandler = () => {
      // ダイアログを閉じる
      refDialog.value = new MyListFolderPageDialogType(0, "", 0);
    };
    const deleteHandler = () => {
      const recipeId = refDialog.value.recipeId;
      switch (refDialog.value.type) {
        case 2:
          refDialog.value = new MyListFolderPageDialogType(0, "", 0);
          deleteFolder();
          break;
        case 4:
          refDialog.value = new MyListFolderPageDialogType(0, "", 0);
          deleteRecipe(recipeId);
          break;
        default:
          break;
      }
    };

    // フォルダ操作選択のダイアログ
    const showFolderOperation = computed(() => {
      if (refDialog.value.type == 1) {
        return true;
      }
      return false;
    });
    const folderOperationHandler = (selectNumber: number) => {
      NativeClient.sendDebugLog("MyListFolderPage select = " + selectNumber);
      refDialog.value = new MyListFolderPageDialogType(0, "", 0);
      switch (selectNumber) {
        case 0:
          // フォルダ削除
          refDialog.value = new MyListFolderPageDialogType(2, "", 0);
          break;
        case 1:
          // フォルダ名変更
          updateInputFolderName();
          refDialog.value = new MyListFolderPageDialogType(3, "", 0);
          break;
        default:
          break;
      }
    };

    // フォルダ名変更ダイアログ
    const showRenameFolderDialog = computed(() => {
      if (refDialog.value.type == 3) {
        // Beacon Log
        const logInfo = new BeaconLogInfo(PAGE_ID.DIALOG, null, ["お気に入りフォルダ変更"]);
        NativeClient.sendViewedLog(logInfo);

        return true;
      }
      return false;
    });
    const folderName = computed(() => {
      return refCurrentFolderName.value;
    });
    const closeRenameFolderDialogHandler = () => {
      // ダイアログを閉じる
      refDialog.value = new MyListFolderPageDialogType(0, "", 0);
    };
    const renameFolderHandler = (folderName: string) => {
      refDialog.value = new MyListFolderPageDialogType(0, "", 0);
      if (folderName.length > COMMON_LIMIT_NUM.FAVORITES_FOLDER_NAME_LIMIT) {
        refDialog.value = new MyListFolderPageDialogType(8, "", 0);
        return;
      }
      renameFolder(folderName);
    };

    // 操作完了メッセージのダイアログ
    const refCompleteOKDialogMessage = ref("");
    const showOKDialog = computed(() => {
      let showOKDialog = false;
      switch (refDialog.value.type) {
        case 5:
          showOKDialog = true;
          refCompleteOKDialogMessage.value = dialogMessageCompleteFolderDelete;
          break;
        case 6:
          showOKDialog = true;
          refCompleteOKDialogMessage.value = dialogMessageForCompleteRecipeDelete;
          break;
        case 7:
          showOKDialog = true;
          refCompleteOKDialogMessage.value = dialogMessageForRecipeLimitOver;
          break;
        case 8:
          showOKDialog = true;
          refCompleteOKDialogMessage.value = dialogMessageForLimitOverFolderName;
          break;
        default:
          break;
      }
      return showOKDialog;
    });
    const completeOKDialogMessage = computed(() => {
      return refCompleteOKDialogMessage.value;
    });
    const completeOKDialogHandler = () => {
      const type = refDialog.value.type;
      NativeClient.sendDebugLog("MyListFolderPage completeOKDialogHandler");
      // ダイアログを閉じる
      refDialog.value = new MyListFolderPageDialogType(0, "", 0);

      // 種類により処理を変更する
      switch (type) {
        case 5:
          // フォルダが削除されたので、前ページに戻る
          router.go(-1);
          break;
        case 6:
          // クリアしてレシピ一覧を再取得する
          NativeClient.sendDebugLog("MyListFolderPage completeOKDialogHandler receipe delete complete");
          clearList();
          getRecipeList();
          break;
        case 7:
          break;
        default:
          break;
      }
    };

    // APIリクエスト時のエラーハンドル
    const {
      getApiErrorTitle,
      getApiErrorMessage
    } = useApiErrorHandler();
    const { closeDialog } = useApiErrorDialog();
    const showApiErrorDialog = computed(() => {
      const apiError: ApiError = store.getters["common/getApiError"];
      NativeClient.sendDebugLog("MyListFolderPage showApiError");
      if (apiError == null) {
        NativeClient.sendDebugLog("MyListFolderPage showApiError no error");
        return false;
      } else if (!isDisplayed) {
        NativeClient.sendDebugLog("MyListFolderPage showApiError isDisplayed is false");
        return false;
      }
      return true;
    });
    const errorCancelHandler = () => {
      closeDialog();
      refRequestType.value = new MyListFolderPageRequestType(0, "", 0);
    };
    const retryHandler = () => {
      closeDialog();
      switch (refRequestType.value.type) {
        case 1:
          getRecipeList();
          break;
        case 2:
          deleteFolder();
          break;
        case 3:
          renameFolder(refRequestType.value.folderName);
          break;
        case 4:
          deleteRecipe(refRequestType.value.recipeId);
          break;
        default:
          break;
      }
    };

    // 追加ボタン
    const appendFolderAction = () => {
      NativeClient.sendDebugLog("MyListFolderPage Append");
      if (totalCount >= 99) {
        refDialog.value = new MyListFolderPageDialogType(7, "", 0);
      } else {
        router.push(`/mylist-select?folderId=` + currentId);
      }
    };
    // フォルダー操作
    const folderOperationAction = () => {
      NativeClient.sendDebugLog("MyListFolderPage operation");
      refDialog.value = new MyListFolderPageDialogType(1, "", 0);
    };
    // レシピ選択
    const selectRecipeHandler = (recipeId: number, type: string) => {
      NativeClient.sendDebugLog("MyListFolderPage selectRecipe");
      NativeClient.transitionReceipDetail(recipeId, type);
    };
    // レシピ削除
    const deleteRecipeHandler = (recipeId: number) => {
      NativeClient.sendDebugLog("MyListFolderPage delete Recipe");
      refDialog.value = new MyListFolderPageDialogType(4, "", recipeId);
    };

    return {
      onScroll,
      recipeList,
      deleteMode,
      selectRecipeHandler,
      deleteRecipeHandler,
      styleFolderOperation,
      styleBodySpacer,
      appendFolderAction,
      folderOperationAction,
      operationAppendText,
      showDialog,
      deleteMessage,
      okText,
      cancelHandler,
      deleteHandler,
      showOKDialog,
      completeOKDialogMessage,
      completeOKDialogHandler,
      showFolderOperation,
      folderOperationHandler,
      showRenameFolderDialog,
      closeRenameFolderDialogHandler,
      folderName,
      renameFolderHandler,
      showApiErrorDialog,
      getApiErrorTitle,
      getApiErrorMessage,
      errorCancelHandler,
      retryHandler,
      updateDisplayKey,
      updateInputFolderNameKey
    };
  }
});
